import React, { useEffect, useState } from "react";
import { Wallet, Search, AlertTriangle, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num, fmtDate } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, StatCard, Badge } from "@/components/common";

const FILTERS = [["all", "Semua"], ["overdue", "Jatuh Tempo"], ["due_soon", "Segera Tempo"]];

export default function Receivables() {
  const { branch, isOwner } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = { filter };
      if (branch) params.branch_id = branch.id;
      if (q) params.q = q;
      setData(await api.get("/receivables", params));
    } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [branch, filter, q]);

  const delOrder = async (o) => {
    if (!window.confirm(`Hapus permanen order ${o.order_no}? Omset, komisi & piutangnya ikut terhapus. Tidak bisa dibatalkan.`)) return;
    try { await api.del(`/orders/${o.id}`); toast.success("Order dihapus"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const s = data?.summary;

  return (
    <>
      <PageHeader title="Piutang (AR)" subtitle={branch?.name} icon={Wallet} />

      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Piutang" value={rupiah(s.total)} sub={`${s.orders} order · ${s.customers} pelanggan`} tone="primary" />
          <StatCard label="Jatuh Tempo" value={rupiah(s.overdue_total)} sub={`${s.overdue_orders} order`} tone="danger" icon={AlertTriangle} />
          <StatCard label="Segera Tempo" value={num(s.due_soon_orders)} tone="warning" />
          <StatCard label="Pelanggan" value={num(s.customers)} />
        </div>
      )}

      <Card className="p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari pelanggan / no order..." className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-300 outline-none focus:border-primary" data-testid="ar-search" />
        </div>
        <div className="flex gap-2">
          {FILTERS.map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} className={`px-3 h-9 rounded-xl text-sm font-medium ${filter === v ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
          ))}
        </div>
      </Card>

      {loading ? <Loading /> : !data?.customers?.length ? (
        <EmptyState icon={Wallet} title="Tidak ada piutang" message="Semua tagihan sudah lunas." />
      ) : (
        <div className="space-y-3">
          {data.customers.map((c) => (
            <Card key={c.key} className="p-4" data-testid={`ar-customer-${c.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800">{c.name}</div>
                  <div className="text-xs text-slate-400">{c.phone || "-"} · {c.orders.length} order · tertua {c.oldest_days} hari</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-slate-800">{rupiah(c.outstanding)}</div>
                  {c.overdue > 0 && <Badge className="bg-rose-100 text-rose-700 mt-1">Tempo {rupiah(c.overdue)}</Badge>}
                  {c.over_limit && <Badge className="bg-amber-100 text-amber-700 mt-1 ml-1">Over limit</Badge>}
                </div>
              </div>
              <div className="mt-3 border-t border-slate-100 pt-2 space-y-1.5">
                {c.orders.map((o) => (
                  <div key={o.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`ar-order-${o.id}`}>
                    <div className="min-w-0 text-slate-600 truncate">
                      {o.order_no} · {fmtDate(o.created_at)} {o.is_overdue && <span className="text-rose-500">· telat {o.overdue_days}h</span>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-medium text-slate-700">{rupiah(o.balance_due)}</span>
                      {isOwner && <button onClick={() => delOrder(o)} className="w-7 h-7 rounded-lg hover:bg-rose-50 flex items-center justify-center text-rose-500" data-testid={`delete-ar-${o.id}`} title="Hapus order (Owner)"><Trash2 size={14} /></button>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
